const WIDTH = 100;
const HEIGHT = 40;

type Point = [number, number];

function getSmoothPath(points: Point[]) {
  const slopes = points.slice(1).map(([x, y], index) => {
    const [prevX, prevY] = points[index] ?? [x, y];
    return (y - prevY) / (x - prevX);
  });
  const tangents = points.map((_, index) => {
    const before = slopes[index - 1];
    const after = slopes[index];

    if (before === undefined) return after ?? 0;
    if (after === undefined) return before;

    return before * after <= 0 ? 0 : (2 * before * after) / (before + after);
  });

  return points.reduce((path, [x, y], index) => {
    const prev = points[index - 1];

    if (!prev) return `M${x},${y}`;

    const [prevX, prevY] = prev;
    const step = (x - prevX) / 3;
    const startTangent = tangents[index - 1] ?? 0;
    const endTangent = tangents[index] ?? 0;

    return `${path} C${prevX + step},${prevY + startTangent * step} ${x - step},${y - endTangent * step} ${x},${y}`;
  }, '');
}

export function Sparkline({ id, values }: { id: string; values: number[] }) {
  const gradientId = `sparkline-${id}`;

  if (values.length < 2) return null;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const points = values.map(
    (value, index): Point => [
      (index / (values.length - 1)) * WIDTH,
      HEIGHT - ((value - min) / range) * (HEIGHT * 0.8) - HEIGHT * 0.1,
    ],
  );
  const line = getSmoothPath(points);

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      preserveAspectRatio="none"
      aria-hidden
      className="pointer-events-none absolute bottom-0 left-0 h-1/2 w-full text-green"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity={0.06} />
          <stop offset="100%" stopColor="currentColor" stopOpacity={0} />
        </linearGradient>
      </defs>
      <path
        d={`${line} L${WIDTH},${HEIGHT} L0,${HEIGHT} Z`}
        fill={`url(#${gradientId})`}
      />
      <path
        d={line}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeOpacity={0.3}
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
